import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import TableContainer from '@hud/web-components/Container/TableContainer';
import { DefaultTable } from '@hud/web-components/Table/Table';
import { Search } from '@hud/web-components/Search/Search';

import { Job, JobEndpoint } from '@hud/portal-dtos/pr-comparisons.dto';
import Pagination from '@hud/web-components/Pagination/Pagination';
import {
  ColumnFiltersState,
  PaginationState,
  SortingState,
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import LongContentPopover from '@hud/web-components/LongContentPopover/LongContentPopover';
import { DEFAULT_PAGE_SIZE } from '../../../consts';
import { HorizontalContainer } from '@hud/web-components/Container/HorizontalContainer';
import { BreadcrumbItem } from '@hud/web-components/Breadcrumbs/Breadcrumbs';
import { formatMetricValue } from '../../../utils';
import SearchRow from '@hud/web-components/SearchRow/SearchRow';
import Menu from '@hud/web-components/Menu/Menu';
import { FiltersIcon, FiltersMenuButton } from '../../../components/FunctionsTable/FunctionsTable.styles';
import { FlagFilter } from '@hud/web-components/Filters/Filters';
import { Skeleton } from '@mantine/core';
import renderEndpointByPath from '../../../components/endpointsCellRenderer/EndpointByPathCellRenderer';

export interface JobEndpointsTableProps {
  job?: Job;
  loading: boolean;
  back?: number;
  endpoints: JobEndpoint[];
  referralBreadcrumbs?: BreadcrumbItem[] | null;
}

const columnHelper = createColumnHelper<JobEndpoint>();

export default function JobEndpointsTable({
  job,
  loading,
  endpoints,
  referralBreadcrumbs,
}: JobEndpointsTableProps) {
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState<SortingState>([
    {
      id: 'degradation_rate',
      desc: true,
    },
  ]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]) // can set initial column filter state here

  const filterDegraded = useMemo(() => {
    return columnFilters.find((f) => f.id === 'degraded')?.value;
  }, [columnFilters]);

  const columns = useMemo(() => {
    return [
      columnHelper.accessor('path', {
        header: 'Path',
        minSize: 200,
        maxSize: 400,
        meta: {},
        cell: renderEndpointByPath(referralBreadcrumbs, {job: job?.id || ''}),
      }),
      columnHelper.accessor('serviceName', {
        header: 'Service',
        minSize: 50,
        maxSize: 150,
        meta: {},
        cell: (cell) => {
          return <LongContentPopover>{cell.getValue()}</LongContentPopover>;
        },
      }),
      columnHelper.accessor('runs', {
        header: 'Runs',
        enableGlobalFilter: false,
        minSize: 60,
        maxSize: 120,
        meta: {
          rightAlign: true,
        },
      }),
      columnHelper.accessor('p90_duration_base', {
        header: 'Base P90',
        enableGlobalFilter: false,
        cell: (cell) => {
          const normalized = formatMetricValue({
            value: cell.getValue(),
            format: 'duration',
          });
          return <LongContentPopover>{normalized}</LongContentPopover>;
        },
        minSize: 60,
        maxSize: 120,
        meta: {
          rightAlign: true,
          fitFormatter: (cell) => {
            if (!cell.getValue()) {
              return '';
            }
            return formatMetricValue({
              value: cell.getValue(),
              format: 'duration',
            });
          },
        },
      }),
      columnHelper.accessor('p90', {
        header: 'PR P90',
        enableGlobalFilter: false,
        cell: (cell) => {
          const normalized = formatMetricValue({
            value: cell.getValue(),
            format: 'duration',
          });
          return <LongContentPopover>{normalized}</LongContentPopover>;
        },
        minSize: 60,
        maxSize: 120,
        meta: {
          rightAlign: true,
          fitFormatter: (cell) => {
            return formatMetricValue({
              value: cell.getValue(),
              format: 'duration',
            });
          },
        },
      }),
      columnHelper.accessor('p90Degradation.value', {
        enableGlobalFilter: false,
        header: 'Delta',
        cell: (cell) => {
          const normalized = formatMetricValue({
            value: cell.getValue(),
            format: 'durationAllowNegative',
          });
          return normalized;
        },
        minSize: 100,
        maxSize: 150,
        meta: {
          copyable: false,
          rightAlign: true,
          fitFormatter: (cell) => {
            if (!cell.getValue()) {
              return '';
            }
            return formatMetricValue({
              value: cell.getValue(),
              format: 'duration',
            });
          },
        },
      }),
      columnHelper.accessor('p90Degradation.rate', {
        enableGlobalFilter: false,
        header: 'Delta %',
        cell: (cell) => {
          const normalized = formatMetricValue({
            value: cell.getValue(),
            format: 'percent',
          });
          return <LongContentPopover>{normalized}</LongContentPopover>;
        },
        minSize: 60,
        maxSize: 120,
        meta: {
          fitFormatter: (cell) => {
            if (!cell.getValue()) {
              return '';
            }
            return formatMetricValue({
              value: cell.getValue(),
              format: 'percent',
            });
          },
          copyable: false,
          rightAlign: true,
        },
      }),
      columnHelper.accessor('errorRate', {
        enableGlobalFilter: false,
        header: 'Error %',
        cell: (cell) => {
          const normalized = formatMetricValue({
            value: cell.getValue(),
            format: 'percent',
          });
          return <LongContentPopover>{normalized}</LongContentPopover>;
        },
        minSize: 60,
        maxSize: 120,
        meta: {
          rightAlign: true,

          fitFormatter: (cell) => {
            if (!cell.getValue()) {
              return '';
            }
            return formatMetricValue({
              value: cell.getValue(),
              format: 'percent',
            });
          },
        },
      }),
      columnHelper.accessor('degraded', {
        filterFn: (row, _columnId, filterValue) => {
          const ep = row.original;
          if (filterValue) {
            return ep.degraded;
          }
          return true;
        },

      }),
    ];
  }, [job, referralBreadcrumbs]);
  const prevEndpoints = useRef(endpoints);

  // after page finally loaded with endpoints, if there is one degraded, set degraded filter to true
  useEffect(() => {
    if ((!prevEndpoints.current || prevEndpoints.current.length === 0) && endpoints.length > 0) {
      prevEndpoints.current = endpoints;
      const hasDegraded = endpoints.some((ep) => ep.degraded);
      if (hasDegraded) {
        handleFilterDegraded(true);
      }
    }
  }, [endpoints]);

  const handleFilterDegraded = useCallback((value: boolean) => {
    if (value) {
      setColumnFilters([
        {
          id: 'degraded',
          value,
        }
      ]);
    } else {
      setColumnFilters([]);
    }
  }, [setColumnFilters]);

  const table = useReactTable({
    columns: columns,
    data: endpoints,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    columnResizeMode: 'onChange',
    enableSortingRemoval: false,
    state: {
      pagination,
      sorting,
      globalFilter,
      columnVisibility: {
        degraded: false,
      },
      columnFilters,
    },
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
  });

  const hasFilters = columnFilters.length > 0;

  return (
    <TableContainer.Container id="endpoints-stats"

      title={loading ? <Skeleton width={150} height={26} /> : `Showing ${table.getPrePaginationRowModel().rows.length}/${endpoints.length}`}
    >
      <TableContainer.Header>
        <SearchRow>
          <Search
            inline
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
          />
          {
            filterDegraded ? (
              <FlagFilter
                label="Show Degraded Only"
                onClear={() => handleFilterDegraded(false)}
              />
            ) : null
          }
          <Menu.Menu>
            <Menu.Target>
              <div>
                <FiltersMenuButton>
                  <FiltersIcon
                    icon={hasFilters ? 'menu.plus' : 'other.filters'}
                    size={hasFilters ? '14px' : '20px'}
                  />
                  {!hasFilters ? 'Filters' : ''}
                </FiltersMenuButton>
              </div>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item onClick={() => handleFilterDegraded(true)}>
                Show Degraded Only
              </Menu.Item>
            </Menu.Dropdown>
          </Menu.Menu>

          {/* {loading ? (
            <Skeleton width={100} />
          ) : (
            <ItemCounter
              total={endpoints.length}
              filtered={table.getPrePaginationRowModel().rows.length}
            />
          )} */}
        </SearchRow>
      </TableContainer.Header>
      <HorizontalContainer>
        <DefaultTable table={table} label="Endpoints stats" loading={loading} />
      </HorizontalContainer>
      <TableContainer.Footer>
        <Pagination
          pages={loading ? 0 : table.getPageCount()}
          currentPage={
            loading || table.getPageCount() === 0
              ? 0
              : table.getState().pagination.pageIndex + 1
          }
          onPageChange={(page) => table.setPageIndex(page - 1)}
          loading={loading}
        />
      </TableContainer.Footer>
    </TableContainer.Container>
  );
}
